import { Link, useNavigate, useLocation } from "react-router-dom";
import CartSidebar from "./common/CartSidebar";
import Login from "./pages/Login";
import { useAuth } from "./hooks/IsAuth";
import ProfileDropdown from "./common/ProfileDropdown";
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { callAPIWithoutAuth } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import { setSearch } from "./slices/searchSlice";
import { headerNotification, removeHeader, selectCompanyInfo, selectContactInfo, selectNumberTitle } from "./slices/footerSlice";
import { loginModelOpen } from "./common/Helper";

function Header() {
  const { isLoggedIn, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { categories, loading: categoriesLoading } = useSelector(
    (state) => state.category || { categories: [], loading: false }
  );

  const contactInfo = useSelector(selectContactInfo);
  const headerN = useSelector(headerNotification);
  const numberTitle = useSelector(selectNumberTitle)
  const companyInfo = useSelector(selectCompanyInfo)

  const {
    cartItems = [],
  } = useSelector((state) => state?.cart || {});

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimerRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const isOnProductsPage = location.pathname.includes('/product-listing')

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlQuery = searchParams.get('query');
    if (urlQuery) {
      setSearchQuery(decodeURIComponent(urlQuery));
      setShowSuggestions(false);
      dispatch(setSearch(decodeURIComponent(urlQuery)));
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    const hasCategory = location.pathname.match(/\/product-listing\/\d+/);
    if (hasCategory) {
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setIsTyping(false);
    }
  }, [location.pathname]);

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
  };

  useEffect(() => {
    if (!isOnProductsPage) {
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setIsTyping(false);
    }
  }, [location.pathname, isOnProductsPage]);

  const closeNavbar = () => {
    const navbarCollapse = document.getElementById('navbarSupportedContent');
    const toggler = document.querySelector('.navbar-toggler');

    if (navbarCollapse && navbarCollapse.classList.contains('show')) {
      navbarCollapse.classList.remove('show');

      if (toggler) {
        toggler.classList.add('collapsed');
        toggler.setAttribute('aria-expanded', 'false');
      }
    }
  };

  const searchProducts = useCallback(async (query) => {
    if (!query || query.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      setLoader(true);
      const response = await callAPIWithoutAuth(
        apiUrls.productSearch,
        { search: query },
        "GET",
        {}
      );
      setLoader(false);
      if (response?.data?.status && response?.data?.data?.length > 0) {
        setSuggestions(response?.data?.data || []);
        setShowSuggestions(true); 
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      setLoader(false);
      console.error("Error fetching products:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []); 

  useEffect(() => {
    if (!isTyping || !searchQuery || searchQuery.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      return;
    }

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      searchProducts(searchQuery);
    }, 400);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchQuery, isTyping]);

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);
    setIsTyping(true);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      if (isOnProductsPage && searchQuery.trim() !== "") {
        e.preventDefault();
        dispatch(setSearch(searchQuery));
        navigate(`/product-listing?query=${encodeURIComponent(searchQuery)}`);
        setShowSuggestions(false);
        setIsTyping(false);
      } else {
        applySearch();
        setSearchOpen(false);
      }
    }
  };

  const applySearch = () => {
    setIsTyping(false);
    setShowSuggestions(false);
    if (searchQuery.trim() === "") {
      dispatch(setSearch(""));
      navigate('/product-listing');
    } else {
      dispatch(setSearch(searchQuery));
      navigate(`/product-listing?query=${encodeURIComponent(searchQuery)}`);
    }
    setSearchOpen(false);
  };

  const handleSearchClick = () => {
    if (searchQuery.trim() !== "") {
      applySearch();
    }
  };

  const handleSuggestionClick = (product) => {
    setIsTyping(false);
    setShowSuggestions(false);
    setSuggestions([]);
    if (isOnProductsPage) {
      dispatch(setSearch(searchQuery));
      navigate(`/product-detail/${product?.id}`);
    } else {
      navigate(`/product-detail/${product?.id}`);
    }
    setSearchQuery('');
    setSearchOpen(false);
  };

  const handleInputFocus = () => {
    if (searchQuery && searchQuery.trim().length > 0 && suggestions.length > 0) {
      setIsTyping(true);
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsTyping(false);
      setShowSuggestions(false);
    }, 200);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setIsTyping(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchWrapper = document.querySelector('.search-wrapper');
      if (searchWrapper && !searchWrapper.contains(event.target)) {
        setIsTyping(false);
        setShowSuggestions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleCategoryClick = (category, e) => {
    e.preventDefault();
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setIsTyping(false);
    navigate(`/product-listing/${category?.id}`);
  };

  const handleRemoveHeader = () => {
    dispatch(removeHeader());
  };

  return (
    <>
      <header>
        {headerN && <div className="top_header">
          <div className="container">
            <div className="position-relative font--15 text-center">
              <img src="/image/h1-flash.png" alt="" className="me-1" /> {headerN}
              <span style={{ cursor: 'pointer' }} onClick={handleRemoveHeader}>X</span>
            </div>
          </div>
        </div>}
        <div className="header-middle">
          <div className="container-fluid space_container">
            <div className="d-flex justify-content-between align-items-center">
              <div className="logo-sec">
                <Link to="/" onClick={() => {
                  setSearchQuery('');
                  setSuggestions([]);
                  setShowSuggestions(false);
                  setIsTyping(false);
                }}>
                  <img alt="" src={companyInfo?.header_logo_url || "/image/logo.png"} />
                </Link>
              </div>
              <div className="search-wrapper d-none d-md-block">
                <div className="form-group position-relative">
                  <span className="input-icon" style={{ cursor: 'pointer' }}>
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.7099 20.29L17.9999 16.61C19.44 14.8144 20.1374 12.5353 19.9487 10.2413C19.76 7.94731 18.6996 5.81279 16.9854 4.27665C15.2713 2.74051 13.0337 1.91951 10.7328 1.98247C8.43194 2.04543 6.24263 2.98757 4.61505 4.61515C2.98747 6.24273 2.04534 8.43204 1.98237 10.7329C1.91941 13.0338 2.74041 15.2714 4.27655 16.9855C5.81269 18.6997 7.94721 19.7601 10.2412 19.9488C12.5352 20.1375 14.8143 19.4401 16.6099 18L20.2899 21.68C20.3829 21.7737 20.4935 21.8481 20.6153 21.8989C20.7372 21.9497 20.8679 21.9758 20.9999 21.9758C21.1319 21.9758 21.2626 21.9497 21.3845 21.8989C21.5063 21.8481 21.6169 21.7737 21.7099 21.68C21.8901 21.4935 21.9909 21.2443 21.9909 20.985C21.9909 20.7257 21.8901 20.4765 21.7099 20.29ZM10.9999 18C9.61544 18 8.26206 17.5895 7.11091 16.8203C5.95977 16.0511 5.06256 14.9579 4.53275 13.6788C4.00293 12.3997 3.86431 10.9922 4.13441 9.63437C4.4045 8.2765 5.07119 7.02922 6.05016 6.05026C7.02912 5.07129 8.27641 4.4046 9.63427 4.13451C10.9921 3.86441 12.3996 4.00303 13.6787 4.53285C14.9578 5.06266 16.051 5.95987 16.8202 7.11101C17.5894 8.26216 17.9999 9.61553 17.9999 11C17.9999 12.8565 17.2624 14.637 15.9497 15.9498C14.6369 17.2625 12.8564 18 10.9999 18Z"
                        fill="#555"
                      />
                    </svg>
                  </span>
                  <input
                    ref={searchInputRef}
                    className="form-control"
                    placeholder="Search products..."
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyDown={handleSearchKeyDown}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="position-absolute bg-transparent border-0"
                      onClick={handleClearSearch}
                      style={{
                        right: '50px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '20px',
                        color: '#999',
                        zIndex: 1002
                      }}
                    >
                      ×
                    </button>
                  )}
                  {loader && isTyping && (
                    <div
                      className="position-absolute text-center py-2 bg-white border rounded"
                      style={{
                        width: "100%",
                        zIndex: 1001,
                        top: "100%",
                        left: 0,
                        color: 'black',
                        marginTop: "5px"
                      }}
                    >
                      Searching...
                    </div>
                  )}
                  {!loader && showSuggestions && isTyping && suggestions?.length > 0 && (
                    <ul
                      className="suggestion-dropdown list-unstyled p-2 shadow-sm"
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        borderRadius: "6px",
                        zIndex: 1000,
                        maxHeight: "300px",
                        overflowY: "auto",
                        marginTop: "5px",
                        background: "white"
                      }}
                    >
                      {suggestions?.map((product, index) => {
                        const getProductImage = (product) => {
                          if (product.windshield_variants &&
                            product.windshield_variants.length > 0 &&
                            product.windshield_variants[0].images &&
                            product.windshield_variants[0].images.length > 0) {
                            return product.windshield_variants[0].images[0].image_url;
                          }
                          else if (product.battery_variants &&
                            product.battery_variants.length > 0 &&
                            product.battery_variants[0].images &&
                            product.battery_variants[0].images.length > 0) {
                            return product.battery_variants[0].images[0].image_url;
                          }
                          else if (product.tyre_variants &&
                            product.tyre_variants.length > 0 &&
                            product.tyre_variants[0].images &&
                            product.tyre_variants[0].images.length > 0) {
                            return product.tyre_variants[0].images[0].image_url;
                          }
                        };
                        return (
                          <li key={product.id || index} className="mb-2">
                            <button
                              onClick={() => handleSuggestionClick(product)}
                              className="d-flex align-items-center text-decoration-none p-3 rounded-3 w-100 border-0"
                              style={{
                                transition: 'all 0.3s ease',
                                background: '#f8f9fa'
                              }}
                            >
                              <img
                                src={getProductImage(product)}
                                alt={product.name || "Product"}
                                width={48}
                                height={48}
                                className="rounded-circle me-3 object-fit-cover border border-2 border-white shadow-sm"
                                style={{ objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
                                }}
                              />
                              <div className="flex-grow-1 text-start">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="fw-medium">
                                    {product?.name || product?.title}
                                  </span>
                                </div>
                                {product.windshield_variants && product.windshield_variants.length > 0 && (
                                  <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                    {product.windshield_variants.length} variant(s) available
                                  </p>
                                )}
                                {product.tyre_variants && product.tyre_variants.length > 0 && (
                                  <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                    {product.tyre_variants.length} variant(s) available
                                  </p>
                                )}
                                {product.battery_variants && product.battery_variants.length > 0 && (
                                  <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                    {product.battery_variants.length} variant(s) available
                                  </p>
                                )}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              <div className="right-menu">
                <div className="d-flex align-items-center justify-content-end">
                  <Link
                    className="header-menu text-decoration-none d-inline-flex d-md-none"
                    onClick={toggleSearch}
                  >
                    <span className="header-menu-icon me-2">
                      <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21.7099 20.29L17.9999 16.61C19.44 14.8144 20.1374 12.5353 19.9487 10.2413C19.76 7.94731 18.6996 5.81279 16.9854 4.27665C15.2713 2.74051 13.0337 1.91951 10.7328 1.98247C8.43194 2.04543 6.24263 2.98757 4.61505 4.61515C2.98747 6.24273 2.04534 8.43204 1.98237 10.7329C1.91941 13.0338 2.74041 15.2714 4.27655 16.9855C5.81269 18.6997 7.94721 19.7601 10.2412 19.9488C12.5352 20.1375 14.8143 19.4401 16.6099 18L20.2899 21.68C20.3829 21.7737 20.4935 21.8481 20.6153 21.8989C20.7372 21.9497 20.8679 21.9758 20.9999 21.9758C21.1319 21.9758 21.2626 21.9497 21.3845 21.8989C21.5063 21.8481 21.6169 21.7737 21.7099 21.68C21.8901 21.4935 21.9909 21.2443 21.9909 20.985C21.9909 20.7257 21.8901 20.4765 21.7099 20.29ZM10.9999 18C9.61544 18 8.26206 17.5895 7.11091 16.8203C5.95977 16.0511 5.06256 14.9579 4.53275 13.6788C4.00293 12.3997 3.86431 10.9922 4.13441 9.63437C4.4045 8.2765 5.07119 7.02922 6.05016 6.05026C7.02912 5.07129 8.27641 4.4046 9.63427 4.13451C10.9921 3.86441 12.3996 4.00303 13.6787 4.53285C14.9578 5.06266 16.051 5.95987 16.8202 7.11101C17.5894 8.26216 17.9999 9.61553 17.9999 11C17.9999 12.8565 17.2624 14.637 15.9497 15.9498C14.6369 17.2625 12.8564 18 10.9999 18Z" fill="#555" />
                      </svg>
                    </span>
                  </Link>
                  <Link
                    className="header-menu text-decoration-none"
                    to={isLoggedIn ? "/wishlist" : '#'} onClick={(e) => {
                      if (!isLoggedIn) {
                        e.preventDefault();
                        loginModelOpen();
                      }
                    }}
                  >
                    <span className="header-menu-icon me-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <mask id="mask0_2_68" maskUnits="userSpaceOnUse" x="2" y="3" width="21" height="20">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M2 2.9999H22.4725V22.5009H2V2.9999Z" fill="white" />
                        </mask>
                        <g mask="url(#mask0_2_68)">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M3.82371 12.123C5.22571 16.485 10.7647 20.012 12.2367 20.885C13.7137 20.003 19.2927 16.437 20.6497 12.127C21.5407 9.341 20.7137 5.812 17.4277 4.753C15.8357 4.242 13.9787 4.553 12.6967 5.545C12.4287 5.751 12.0567 5.755 11.7867 5.551C10.4287 4.53 8.65471 4.231 7.03771 4.753C3.75671 5.811 2.93271 9.34 3.82371 12.123ZM12.2377 22.501C12.1137 22.501 11.9907 22.471 11.8787 22.41C11.5657 22.239 4.19271 18.175 2.39571 12.581C2.39471 12.581 2.39471 12.58 2.39471 12.58C1.26671 9.058 2.52271 4.632 6.57771 3.325C8.48171 2.709 10.5567 2.98 12.2347 4.039C13.8607 3.011 16.0207 2.727 17.8867 3.325C21.9457 4.634 23.2057 9.059 22.0787 12.58C20.3397 18.11 12.9127 22.235 12.5977 22.408C12.4857 22.47 12.3617 22.501 12.2377 22.501Z" fill="black" />
                        </g>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M18.1537 10.6249C17.7667 10.6249 17.4387 10.3279 17.4067 9.9359C17.3407 9.1139 16.7907 8.4199 16.0077 8.1669C15.6127 8.0389 15.3967 7.6159 15.5237 7.2229C15.6527 6.8289 16.0717 6.6149 16.4677 6.7389C17.8307 7.1799 18.7857 8.3869 18.9027 9.8139C18.9357 10.2269 18.6287 10.5889 18.2157 10.6219C18.1947 10.6239 18.1747 10.6249 18.1537 10.6249Z" fill="black" />
                      </svg>
                    </span>
                    <span className="header-meny-text">My Favorites</span>
                  </Link>
                  <Link
                    className="header-menu text-decoration-none position-relative border-0 bg-transparent p-0"
                    to="#offcanvasExample"
                    data-bs-toggle="offcanvas"
                    aria-controls="offcanvasExample"
                    data-discover="true"
                  >
                    <span className="header-menu-icon me-2">
                      <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M16.5136 21.5H8.16579C5.09943 21.5 2.74703 20.3924 3.41522 15.9348L4.19325 9.89359C4.60515 7.66934 6.02392 6.81808 7.26877 6.81808H17.4473C18.7104 6.81808 20.0468 7.73341 20.5228 9.89359L21.3008 15.9348C21.8683 19.889 19.58 21.5 16.5136 21.5Z" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16.651 6.5984C16.651 4.21232 14.7167 2.27803 12.3306 2.27803V2.27803C11.1816 2.27316 10.078 2.72619 9.26381 3.53695C8.44963 4.3477 7.99193 5.44939 7.99194 6.5984H7.99194" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M15.2962 11.1018H15.2505" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.46566 11.1018H9.41989" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="header-meny-text d-none d-xl-inline-block">Cart</span>
                    {cartItems?.length > 0 && <span class="cart-number">{cartItems?.length}</span>}
                  </Link>
                  <>
                    {isLoggedIn ? (
                      <ProfileDropdown />
                    ) : (
                      <Link
                        className="header-menu text-decoration-none"
                        data-bs-toggle="modal"
                        data-bs-target="#loginModal"
                        to={"/login"}
                        data-discover="true"
                      >
                        <span className="header-menu-icon me-2">
                          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M11.889 12.6187H11.921C14.849 12.6187 17.23 10.2377 17.23 7.30971C17.23 4.38171 14.849 1.99971 11.921 1.99971C8.99202 1.99971 6.61002 4.38171 6.61002 7.30671C6.60002 10.2267 8.96702 12.6097 11.889 12.6187ZM8.03802 7.30971C8.03802 5.16871 9.78002 3.42771 11.921 3.42771C14.061 3.42771 15.802 5.16871 15.802 7.30971C15.802 9.44971 14.061 11.1917 11.921 11.1917H11.892C9.76002 11.1837 8.03102 9.44371 8.03802 7.30971Z" fill="#000" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M4 18.173C4 21.87 9.962 21.87 11.921 21.87C15.32 21.87 19.84 21.489 19.84 18.193C19.84 14.496 13.88 14.496 11.921 14.496C8.521 14.496 4 14.877 4 18.173ZM5.5 18.173C5.5 16.728 7.66 15.996 11.921 15.996C16.181 15.996 18.34 16.735 18.34 18.193C18.34 19.638 16.181 20.37 11.921 20.37C7.66 20.37 5.5 19.631 5.5 18.173Z" fill="#000" />
                          </svg>
                        </span>
                        <span className="header-meny-text">Sign In</span>
                      </Link>
                    )}
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`modal fade ${searchOpen ? 'show' : ''}`}
          id="searchModal"
          style={{ display: searchOpen ? 'block' : 'none' }}
          tabIndex="-1"
          aria-hidden={!searchOpen}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSearchOpen(false);
              setSearchQuery("");
            }
          }}
        >
          <div className="modal-dialog modal-dialog-top" style={{ marginTop: '110px' }}>
            <div className="modal-content">
              <div className="modal-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="d-flex justify-content-end"></div>
                    <div className="search-section active" id="searchid">
                      <div className="search-form">
                        <form className="search-form-inner">
                          <div className="input-form-group">
                            <label className="form-label m-0 mb-2 font-700">Search</label>
                            <div className="position-relative">
                              <span className="search-input-icon">
                                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M21.7099 20.29L17.9999 16.61C19.44 14.8144 20.1374 12.5353 19.9487 10.2413C19.76 7.94731 18.6996 5.81279 16.9854 4.27665C15.2713 2.74051 13.0337 1.91951 10.7328 1.98247C8.43194 2.04543 6.24263 2.98757 4.61505 4.61515C2.98747 6.24273 2.04534 8.43204 1.98237 10.7329C1.91941 13.0338 2.74041 15.2714 4.27655 16.9855C5.81269 18.6997 7.94721 19.7601 10.2412 19.9488C12.5352 20.1375 14.8143 19.4401 16.6099 18L20.2899 21.68C20.3829 21.7737 20.4935 21.8481 20.6153 21.8989C20.7372 21.9497 20.8679 21.9758 20.9999 21.9758C21.1319 21.9758 21.2626 21.9497 21.3845 21.8989C21.5063 21.8481 21.6169 21.7737 21.7099 21.68C21.8901 21.4935 21.9909 21.2443 21.9909 20.985C21.9909 20.7257 21.8901 20.4765 21.7099 20.29ZM10.9999 18C9.61544 18 8.26206 17.5895 7.11091 16.8203C5.95977 16.0511 5.06256 14.9579 4.53275 13.6788C4.00293 12.3997 3.86431 10.9922 4.13441 9.63437C4.4045 8.2765 5.07119 7.02922 6.05016 6.05026C7.02912 5.07129 8.27641 4.4046 9.63427 4.13451C10.9921 3.86441 12.3996 4.00303 13.6787 4.53285C14.9578 5.06266 16.051 5.95987 16.8202 7.11101C17.5894 8.26216 17.9999 9.61553 17.9999 11C17.9999 12.8565 17.2624 14.637 15.9497 15.9498C14.6369 17.2625 12.8564 18 10.9999 18Z" fill="#555" />
                                </svg>
                              </span>
                              <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder="Search products..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onKeyDown={handleSearchKeyDown}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                              />
                              {searchQuery && (
                                <button
                                  type="button"
                                  className="position-absolute bg-transparent border-0"
                                  onClick={handleClearSearch}
                                  style={{
                                    right: '40px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    color: '#999',
                                    zIndex: 1002
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </form>
                        {loader && isTyping && (
                          <div className="text-center py-2 bg-white border rounded mt-2" style={{ color: 'black' }}>Searching...</div>
                        )}
                        {!loader && showSuggestions && isTyping && suggestions?.length > 0 && (
                          <ul className="suggestion-dropdown list-unstyled p-2 shadow-sm mt-2" style={{ borderRadius: "6px", maxHeight: "300px", overflowY: "auto", background: "white" }}>
                            {suggestions?.map((product, index) => {
                              const getProductImage = (product) => {
                                if (product.windshield_variants?.length > 0 && product.windshield_variants[0].images?.length > 0) return product.windshield_variants[0].images[0].image_url;
                                else if (product.battery_variants?.length > 0 && product.battery_variants[0].images?.length > 0) return product.battery_variants[0].images[0].image_url;
                                else if (product.tyre_variants?.length > 0 && product.tyre_variants[0].images?.length > 0) return product.tyre_variants[0].images[0].image_url;
                                return 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
                              };
                              return (
                                <li key={product.id || index} className="mb-2">
                                  <button onClick={() => { handleSuggestionClick(product); setSearchOpen(false); }} className="d-flex align-items-center text-decoration-none p-3 rounded-3 w-100 border-0" style={{ transition: 'all 0.3s ease', background: '#f8f9fa' }}>
                                    <img src={getProductImage(product)} alt={product.name || "Product"} width={48} height={48} className="rounded-circle me-3 object-fit-cover border border-2 border-white shadow-sm" style={{ objectFit: 'cover' }} onError={(e) => { e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png'; }} />
                                    <div className="flex-grow-1 text-start">
                                      <div className="d-flex align-items-center justify-content-between"><span className="fw-medium">{product?.name || product?.title}</span></div>
                                      {product.windshield_variants?.length > 0 && <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>{product.windshield_variants.length} variant(s) available</p>}
                                      {product.tyre_variants?.length > 0 && <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>{product.tyre_variants.length} variant(s) available</p>}
                                    </div>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bottom_nav">
          <nav className="navbar navbar-expand-lg p-0">
            <div className="container-fluid space_container">
              <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation" id="cliseNav">
                <span className="navbar-toggler-icon" />
              </button>
              <div className="collapse navbar-collapse" id="navbarSupportedContent">
                <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                  <li className="nav-item"><Link className="nav-link" to="/" onClick={() => closeNavbar()}>Home</Link></li>
                  {categoriesLoading ? (<span className=""></span>) : categories && categories.length > 0 ? (categories.map((category, index) => (<li key={index} className="nav-item"><Link className="nav-link" to="#" onClick={(e) => { handleCategoryClick(category, e); closeNavbar(); }}>{category?.title}</Link></li>))) : (<p></p>)}
                  <li className="nav-item"><Link className="nav-link active" to="/pre-order" onClick={() => closeNavbar()}>Pre-order</Link></li>
                  <li className="nav-item"><Link className="nav-link active" to="/about" onClick={() => closeNavbar()}>About Us</Link></li>
                </ul>
                <div className="d-lg-flex d-none">
                  <Link className="nav-link" to="#"><img src="/image/calling.svg" alt="" className="me-1" /> {numberTitle}:&nbsp;&nbsp;<span className="text-decoration-underline">{contactInfo.support_info}</span></Link>
                </div>
              </div>
              <Link className="nav-link contact_us" to="/contact" onClick={() => closeNavbar()}>Contact Us</Link>
            </div>
          </nav>
        </div>
      </header>
      <CartSidebar />
      <Login />
    </>
  );
}

export default Header;